import { Controller } from "@hotwired/stimulus"
import lottie from "lottie-web"

/**
 * Lottie animation controller
 *
 * Usage:
 *   <div data-controller="lottie"
 *        data-lottie-src-value="/lottie/success-checkmark.json"
 *        data-lottie-loop-value="false"
 *        data-lottie-autoplay-value="true"
 *        data-lottie-theme-color-value="#74CEC5">
 *   </div>
 */
export default class extends Controller {
  static values = {
    src: String,
    loop: { type: Boolean, default: false },
    autoplay: { type: Boolean, default: true },
    themeColor: String
  }

  connect() {
    this.loadAnimation()
  }

  async loadAnimation() {
    try {
      let animationData = await this.fetchAnimationData()

      if (this.hasThemeColorValue && this.themeColorValue) {
        animationData = this.applyThemeColor(animationData)
      }

      this.animation = lottie.loadAnimation({
        container: this.element,
        renderer: 'svg',
        loop: this.loopValue,
        autoplay: this.autoplayValue,
        animationData: animationData
      })
    } catch (error) {
      console.error('Failed to load Lottie animation:', error)
    }
  }

  async fetchAnimationData() {
    const response = await fetch(this.srcValue)
    if (!response.ok) {
      throw new Error(`Failed to fetch animation: ${response.status}`)
    }
    return response.json()
  }

  applyThemeColor(data) {
    const hex = this.themeColorValue.replace('#', '')
    const r = parseInt(hex.substring(0, 2), 16) / 255
    const g = parseInt(hex.substring(2, 4), 16) / 255
    const b = parseInt(hex.substring(4, 6), 16) / 255

    return this.replaceColors(JSON.parse(JSON.stringify(data)), [r, g, b])
  }

  replaceColors(obj, rgb) {
    if (Array.isArray(obj)) {
      return obj.map(item => this.replaceColors(item, rgb))
    }

    if (obj && typeof obj === 'object') {
      for (const key in obj) {
        if (key === 'c' && obj[key]?.k && Array.isArray(obj[key].k)) {
          // Lottie stores colors as [r, g, b, a] arrays in "c" properties
          // Skip white colors (preserve white fills)
          const currentColor = obj[key].k
          if (this.isWhiteColor(currentColor)) {
            continue
          }
          obj[key].k = [...rgb, 1]
        } else {
          obj[key] = this.replaceColors(obj[key], rgb)
        }
      }
    }

    return obj
  }

  isWhiteColor(colorArray) {
    // Check if color is white or near-white (r, g, b all >= 0.95)
    if (!Array.isArray(colorArray) || colorArray.length < 3) return false
    return colorArray[0] >= 0.95 && colorArray[1] >= 0.95 && colorArray[2] >= 0.95
  }

  disconnect() {
    if (this.animation) {
      this.animation.destroy()
    }
  }
}
